import { useCallback, useEffect, useState } from 'react';
import type { NewsItem } from '@/types';
import { NEWS_ITEMS } from '@/data/mockData';
import { getApiPath } from '@/lib/api';

export interface LiveNewsResult {
  items: NewsItem[];
  updatedAt: string | null;
  isLive: boolean; // false = 使用 mock 兜底数据
}

const REFRESH_INTERVAL = 60_000; // 1 分钟

export function useLiveNews() {
  const [data, setData] = useState<LiveNewsResult>({
    // 首次请求期间展示加载状态，避免把过期的备用稿误认为实时快讯。
    items: [],
    updatedAt: null,
    isLive: false,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const endpoint = getApiPath('news');
      const res = await fetch(endpoint, { cache: 'no-store' });
      if (!res.ok) {
        console.warn('[news] data request failed', {
          endpoint,
          status: res.status,
        });
        throw new Error(`HTTP ${res.status}`);
      }
      const json = await res.json();
      const items: NewsItem[] = Array.isArray(json.items) && json.items.length > 0
        ? json.items
        : NEWS_ITEMS; // API 返回空列表时也用 mock 兜底
      const hasLiveItems = Array.isArray(json.items) && json.items.length > 0;
      setData({
        items,
        updatedAt: json.updatedAt ?? new Date().toISOString(),
        isLive: hasLiveItems,
      });
      setError(
        json.source === 'unavailable'
          ? '实时快讯暂时无法更新，当前显示精编备用内容。'
          : null,
      );
    } catch {
      // 网络失败：保留当前数据（已有精编备用内容），并明确告知用户状态。
      setData(prev => ({ ...prev, updatedAt: prev.updatedAt }));
      setError(
        data.items.length > 0
          ? '实时快讯暂时无法更新，当前保留可用内容。'
          : '实时快讯暂时无法更新，稍后可点击刷新重试。',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const timer = setInterval(fetchData, REFRESH_INTERVAL);
    return () => clearInterval(timer);
  }, [fetchData]);

  return { ...data, loading, error, refetch: fetchData };
}
